import { useCallback } from 'react';

import { type useEditorPreset } from '@atlassian/conversation-assistant-content-renderer/controllers/editor';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { formatUrlsAsNodes } from '../utils/chat-url-insertion/formatUrlsAsNodes';
import { getExistingUrlsFromDocument } from '../utils/chat-url-insertion/getExistingUrlsFromDocument';
import { insertUrlsIntoDocument } from '../utils/chat-url-insertion/insertUrlsIntoDocument';

type EditorApiType = NonNullable<ReturnType<typeof useEditorPreset>['editorApi']>;

/**
 * Hook for handling URL insertion into the chat prompt editor.
 * Provides a callback that deduplicates URLs, formats them, and inserts them into the document.
 *
 * @param editorApi - The editor API
 * @returns An object with an insertUrls callback function
 */
export const useUrlInsertion = (
	editorApi: EditorApiType | undefined,
): {
	insertUrls: (urls: string[] | undefined, source: string) => void;
} => {
	const { sendAnalyticsEvent } = useAnalytics();

	/**
	 * Inserts URLs into the document.
	 * @param urls - The URLs to insert.
	 */
	const insertUrls = useCallback(
		(urls: string[] | undefined, source: string): void => {
			if (!urls || !Array.isArray(urls)) {
				return;
			}

			try {
				editorApi?.core?.actions?.requestDocument((currentDoc) => {
					if (!currentDoc) {
						throw new Error('Current document is not found');
					}

					const existingUrls = getExistingUrlsFromDocument(currentDoc);
					const newUrls = urls.filter((url) => !existingUrls.has(url));

					if (newUrls.length === 0) {
						editorApi?.core?.actions?.focus();
						return;
					}

					const urlNodes = formatUrlsAsNodes(newUrls);
					const updatedContent = insertUrlsIntoDocument(currentDoc.content || [], urlNodes);

					const updatedDoc = {
						type: 'doc',
						version: 1,
						content: updatedContent,
					};

					const success = editorApi?.core?.actions?.replaceDocument(updatedDoc);
					if (!success) {
						throw new Error('Failed to insert URLs into document');
					}
					editorApi?.core?.actions?.focus();

					sendAnalyticsEvent({
						eventType: 'track',
						action: 'inserted',
						actionSubject: 'link',
						actionSubjectId: 'insertLinksIntoPromptInput',
						attributes: {
							urlCount: newUrls.length,
							source,
						},
					});
				});
			} catch (error) {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'failed',
					actionSubject: 'link',
					attributes: {
						source,
						error: error instanceof Error ? error.message : String(error),
					},
				});
			}
		},
		[editorApi, sendAnalyticsEvent],
	);

	return {
		insertUrls,
	};
};
